import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/use-auth";
import { useFleetPermissions } from "@/hooks/use-fleet-permissions";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import { usePendingPlans, useApprovePlan, useRejectPlan } from "@/hooks/use-maintenance-approvals";
import { ApprovalTable } from "@/components/fleet/ApprovalTable";
import { AlertCircle, TrendingUp, Wrench, DollarSign, Car } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Navigate } from "react-router-dom";

export default function FleetManagerDashboard() {
  const { activeCompany } = useAuth();
  const { canApprove, isFleetManager } = useFleetPermissions();
  const { data: moduleAccess, isLoading: checkingAccess } = useModuleAccess('fleet');
  const companyId = activeCompany?.id;

  // TODOS os hooks devem vir ANTES de qualquer return condicional
  const { data: pendingPlans = [], isLoading: loadingPlans } = usePendingPlans(companyId);
  const approveMutation = useApprovePlan(companyId || '');
  const rejectMutation = useRejectPlan(companyId || '');

  // Buscar métricas gerenciais
  const { data: metrics } = useQuery({
    queryKey: ["fleetMetrics", companyId],
    queryFn: async () => {
      if (!companyId) return null;

      // Total de veículos
      const { count: vehiclesCount } = await supabase
        .from("fleet_vehicles")
        .select("*", { count: "exact", head: true })
        .eq("company_id", companyId)
        .eq("status", "active");

      // OSs abertas
      const { count: openWorkOrdersCount } = await supabase
        .from("fleet_work_orders")
        .select("*", { count: "exact", head: true })
        .eq("company_id", companyId)
        .in("status", ["open", "in_progress"]);

      // Custo total do mês
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { data: workOrders } = await supabase
        .from("fleet_work_orders")
        .select("total_cost")
        .eq("company_id", companyId)
        .gte("created_at", startOfMonth.toISOString());

      const monthlyCost = workOrders?.reduce((sum, wo) => sum + (wo.total_cost || 0), 0) || 0;

      // Veículos em manutenção (com OS em andamento)
      const { data: vehiclesInMaintenance } = await supabase
        .from("fleet_work_orders")
        .select("vehicle_id")
        .eq("company_id", companyId)
        .eq("status", "in_progress");

      const uniqueVehiclesInMaintenance = new Set(
        vehiclesInMaintenance?.map(wo => wo.vehicle_id) || []
      ).size;

      return {
        totalVehicles: vehiclesCount || 0,
        openWorkOrders: openWorkOrdersCount || 0,
        monthlyCost,
        vehiclesInMaintenance: uniqueVehiclesInMaintenance,
      };
    },
    enabled: !!companyId,
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const isActionLoading = approveMutation.isPending || rejectMutation.isPending;

  // Verificar acesso ao módulo e permissões (DEPOIS de todos os hooks)
  if (checkingAccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verificando permissões...</p>
        </div>
      </div>
    );
  }

  // Redirecionar se não tiver acesso ao módulo ou permissão
  if (!moduleAccess?.hasAccess || (!canApprove && !isFleetManager)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold">Dashboard Gerencial</h1>
        <p className="text-muted-foreground">
          Visão geral da frota e aprovação de planos de manutenção
        </p>
      </div>

      {/* Cards de Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total de Veículos</CardTitle>
            <Car className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.totalVehicles || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Veículos ativos na frota</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Planos Pendentes</CardTitle>
            <AlertCircle className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-warning">{pendingPlans.length}</div>
            <p className="text-xs text-muted-foreground mt-1">Aguardando aprovação</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">OSs Abertas</CardTitle>
            <Wrench className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.openWorkOrders || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Ordens de serviço em aberto</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Custo Mensal</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(metrics?.monthlyCost || 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Manutenções do mês</p>
          </CardContent>
        </Card>
      </div>

      {/* Card de Status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Veículos em Manutenção</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-8 w-8 text-primary" />
              <div>
                <div className="text-3xl font-bold">{metrics?.vehiclesInMaintenance || 0}</div>
                <p className="text-sm text-muted-foreground">Com OS em andamento</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Resumo Rápido</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Taxa de manutenção</span>
                <span className="font-medium">
                  {metrics?.totalVehicles
                    ? `${((metrics.vehiclesInMaintenance / metrics.totalVehicles) * 100).toFixed(1)}%`
                    : "0%"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Custo médio por veículo</span>
                <span className="font-medium">
                  {metrics?.totalVehicles
                    ? formatCurrency((metrics.monthlyCost || 0) / metrics.totalVehicles)
                    : formatCurrency(0)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Planos aguardando aprovação</span>
                <span className="font-medium">{pendingPlans.length}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabela de Aprovações */}
      <Card>
        <CardHeader>
          <CardTitle>
            Planos Aguardando Aprovação ({pendingPlans.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingPlans ? (
            <div className="text-center py-8 text-muted-foreground">Carregando...</div>
          ) : (
            <ApprovalTable
              plans={pendingPlans}
              onApprove={(id) => approveMutation.mutate(id)}
              onReject={(id, reason) => rejectMutation.mutate({ planId: id, reason })}
              isLoading={isActionLoading}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
